import { z } from "zod";
import { scriptSentences } from "./language-source.js";
import { inlinePauseSeconds, spokenText } from "./speech.js";

/**
 * The voice over a launch video.
 *
 * Written as one continuous read rather than per-beat clips. A launch film is
 * a single argument delivered over fifty seconds, and cutting the voice into
 * six pieces means six places where the join can be heard — whereas a
 * continuous read over cut picture is what every real one sounds like.
 */

/**
 * The model that speaks it.
 *
 * `eleven_v4` by default, which is the newest and the one asked for. Settable,
 * because a deployment may want the older reads or the cheaper ones — and
 * because the id matters: `eleven_multilingual_v3` and
 * `eleven_multilingual_v4` do not exist, while `eleven_v3` and `eleven_v4`
 * both do. Guessing the pattern from the v2 name gets it wrong.
 */
export const defaultNarrationModel = "eleven_v4";

/**
 * The voice model narration is written for and spoken in.
 *
 * One expression, shared by the worker and the API, so a revision's word
 * budget is the budget the voice-over is then read against, and the limit the
 * page quotes is that same one. Guarded because this package also runs in the
 * browser, where there is no `process`.
 */
export function narrationModelId(): string {
  const configured = typeof process === "undefined" || !process.env ? undefined : process.env.ELEVENLABS_MODEL_ID;
  return configured?.trim() || defaultNarrationModel;
}

/**
 * How fast each model actually reads, in words per second.
 *
 * Measured, not assumed: the same fourteen-word line took 4.96s on
 * multilingual v2, 5.72s on v3 and 6.43s on v4 — a thirty per cent spread. A
 * script sized for one model overruns on another, and the whole job of this
 * number is to stop narration running past the end of the picture.
 *
 * Conservative on purpose. Over-running is the failure that is heard; coming
 * in short just leaves a beat of silence at the end, which is how films end
 * anyway.
 */
const PACE: Record<string, number> = {
  eleven_v4: 2.1,
  eleven_v3: 2.4,
  eleven_multilingual_v2: 2.7,
  eleven_turbo_v2_5: 2.7,
  eleven_flash_v2_5: 2.7,
};

/** An unknown model is assumed to be a slow one, for the reason above. */
export function narrationPace(modelId: string): number {
  return PACE[modelId] ?? 2.1;
}

/**
 * How many words fit in a film of this length.
 *
 * Nine tenths of the running time, because a voice that starts on the first
 * frame and stops on the last is relentless — the close needs a moment with
 * nobody talking over it, which is the point of the close.
 */
/**
 * Deliberately not the shared `narrationWordBudget`.
 *
 * That one assumes 2.5 words a second for every voice, which the measurement
 * above contradicts: v4 reads at about 2.1. Its number is a reasonable guess
 * and it governs narration for ordinary projects, where a scene's duration is
 * re-measured after synthesis anyway — changing it would change every existing
 * video's cut, which is not a thing to do while adding a feature.
 *
 * A launch video has no such second pass. The picture is a fixed length
 * written by a model, so the voice has to fit the first time.
 */
export function launchWordBudget(seconds: number, modelId: string): number {
  return Math.max(12, Math.round(seconds * 0.9 * narrationPace(modelId)));
}

export const launchNarrationSchema = z.object({
  /**
   * The read, as the film's six parts in order: hook, problem, turn, product,
   * proof, close. Each is plain prose — no stage directions, no timings, no
   * labels: the synthesiser reads every character. Stored joined by a blank
   * line, so each part is a paragraph the voice timeline can find again.
   *
   * Read tolerantly, as a revision is: a draft of five or seven parts, or with
   * one left empty, is still a read — it only means the picture cannot be
   * placed by its parts. Refusing it would leave the film with no voice.
   */
  sections: z
    .array(z.unknown())
    .transform((sections) => sections.flatMap((one) => (typeof one === "string" && one.trim() ? [one.trim()] : [])))
    .refine((sections) => sections.length >= 1 && sections.join(" ").length >= 20, "too short to be a read"),
});

/**
 * Trimmed rather than trusted: a word limit in a prompt is a request, and
 * going over is the one failure that is audible.
 *
 * A read that fits is returned as written, its `[1s]` pauses kept. One that
 * does not loses whole sentences from the end — the close first, then the
 * proof — but no part is ever emptied: a part down to its last sentence is
 * passed over, because a missing part means the picture can no longer be
 * placed by the voice. When every part is one sentence and it is still too
 * long, the longest sentence loses words from its end.
 */
export function trimSectionsToBudget(sections: readonly string[], budget: number): string {
  const written = sections.map((section) => section.trim()).filter(Boolean);
  const countWords = (text: string) => text.split(/\s+/).filter(Boolean).length;
  const asWritten = written.join("\n\n");
  if (countWords(spokenText(asWritten)) <= budget) return asWritten;

  const kept = written.map((section) => scriptSentences(section)).filter((sentences) => sentences.length > 0);
  const words = () =>
    kept.reduce((total, sentences) => total + sentences.reduce((sum, one) => sum + countWords(one), 0), 0);
  for (let index = kept.length - 1; index >= 0 && words() > budget; index -= 1) {
    while (kept[index]!.length > 1 && words() > budget) kept[index]!.pop();
  }
  while (words() > budget) {
    let cut = { section: 0, sentence: 0, length: 0 };
    for (const [section, sentences] of kept.entries()) {
      for (const [sentence, one] of sentences.entries()) {
        const length = countWords(one);
        if (length > cut.length) cut = { section, sentence, length };
      }
    }
    if (cut.length <= 1) break;
    const keep = Math.max(1, cut.length - (words() - budget));
    const sentence = kept[cut.section]![cut.sentence]!;
    kept[cut.section]![cut.sentence] = `${sentence
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, keep)
      .join(" ")
      .replace(/[.!?,;:]+$/, "")}.`;
  }
  return kept.map((sentences) => sentences.join(" ")).join("\n\n");
}

export type LaunchNarration = z.infer<typeof launchNarrationSchema>;

/**
 * Words, counted the way the budget counts them, with each inline pause
 * counted as the words its seconds would hold at this pace.
 *
 * Named for the narration rather than generically: `step-diff` already exports
 * a `wordCount` that counts something else, and two functions of the same name
 * meaning different things is how the wrong one gets imported.
 */
export function narrationWordCount(script: string, pace = narrationPace("")): number {
  const words = spokenText(script).split(/\s+/).filter(Boolean).length;
  // A `[1s]` pause is not a word, but it spends the film's time as words do:
  // a second held is the words the voice would have said in it.
  return words + Math.ceil(inlinePauseSeconds(script) * pace);
}
